import { DatePipe } from '@angular/common';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyMenu as MatMenu } from '@angular/material/legacy-menu';
import { InterfaceAppServiceRegister } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { AppHelper } from '@app/core/app.helper';
import { EFieldsTypes } from '@app/core/enums';
import { FormFilterTableControlService } from '@app/modules/table/services/form-filter-table-control.service';
import { ENotifyTypes, NotifierService } from '@services/notifier.service';
import { TuiDestroyService } from '@taiga-ui/cdk';
import { GridsterItem } from 'angular-gridster2';
import * as moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { takeUntil } from 'rxjs';
import { isCharacterInString } from '../../../shared/helpers/text-helpers';
import { DynamicFieldDirective } from '../dynamic-fields/dynamic-field/dynamic-field.directive';
import { InterfaceFactorDateFormat } from '../factor-row/interfaces/factor-row.interface';
import { FactorRowService } from '../factor-row/services/factor-row.service';
import { TableComponent } from '../table/table.component';

@Component({
    selector: 'app-form-filter',
    templateUrl: './form-filter.component.html',
    styleUrls: ['./form-filter.component.scss'],
})
export class FormFilterComponent implements OnInit, OnChanges, AfterViewInit {
    readonly formFilter: FormFilterTableControlService = inject(FormFilterTableControlService);
    /** Объект меню фильтра */
    @ViewChild('menuFilter', { static: false }) private _menuFilter: MatMenu;
    /** Форма */
    #formDynamic: FormGroup;
    readonly #destroy$: TuiDestroyService = inject(TuiDestroyService);

    @Input()
    set formDynamic(form: FormGroup) {
        this.#formDynamic = form;
        this.#onFormChanged();
    }

    get formDynamic(): FormGroup {
        return this.#formDynamic;
    }

    @ViewChildren(DynamicFieldDirective) dynamicFields!: QueryList<DynamicFieldDirective>;
    /** Данные фильтра */
    @Input() settingData: any;
    /** Выполнение фильтрации */
    @Output() setFilter = new EventEmitter<void>();
    /** Сервис взаимодействия страницы  */
    @Input() tableService: any;
    /**  Флаг первого октрытия */
    firstOpen = true;
    /** Сетки полей  */
    fieldGrid: { [key: string]: Array<GridsterItem> } = {};
    /** Сетка сущностей */
    entityGrid: Array<GridsterItem | any>;
    /**  Инициализация формы */
    isInitForm = false;
    /** Наличие адреса в форме */
    isFormAddress = false;
    /** Данные для селекта Регион уже запрошены */
    isRegionRequested = false;
    /** Данные для селекта Регион уже загрузились */
    isRegionLoaded = false;
    /** Загрузка адресов */
    isLoadAddress: {
        region: { [key: string]: boolean };
        district: { [key: string]: boolean };
        city: { [key: string]: boolean };
    } = {
        region: {},
        district: {},
        city: {},
    };
    /** Данные адреса */
    addressData: {
        region?: { id: string; name: string }[];
        district?: { [key: string]: { id: string; name: string }[] };
        city?: { [key: string]: { id: string; name: string }[] };
    } = { region: [], district: {}, city: {} };
    /** Имена адресов */
    addressDataName: { [key: string]: string } = {};
    /** Флаг изменения адреса */
    isSetAddressChange = false;
    /**  Списки */
    registers: { [key: string]: InterfaceAppServiceRegister[] } = {};
    /**  Типы полей */
    fieldsTypes: {
        digit: string; // Число
        bool: string; // Да/Нет
        date: string; // Дата
        string: string; // Строка
        text: string; // Текст
        list: string; // Список
        registry: string; // Список
        address: string; // Адрес
        file: string; // Файл
        gallery: string; // Галерея
        link: string; // Ссылка
        relation: string; // Связь
        reestr: string; // Реестр
        numerator: string; // Нумератор
        access: string; // Доступы
        relationObject: string; // Объект связи
        interaction: string; // Взаимодействие
        technicalProcess: string; // Технический процесс
    } = {
        digit: 'digit',
        bool: 'bool',
        date: 'date',
        string: 'string',
        text: 'text',
        list: 'list',
        registry: 'registry',
        address: 'address',
        file: 'file',
        gallery: 'gallery',
        link: 'link',
        relation: 'relation',
        reestr: 'reestr',
        numerator: 'numerator',
        access: 'access',
        relationObject: 'relation_object',
        interaction: 'interaction',
        technicalProcess: 'technical_process',
    };
    /**
     * Форматы дат
     */
    dateFormat: InterfaceFactorDateFormat = {
        date: '1',
        monthYear: '2',
        year: '3',
        month: '4',
        dateTime: '5',
        dayMonth: '6',
    };

    /**
     * Метод для поиска символов в строке
     * @protected
     */
    protected readonly isCharacterInString = isCharacterInString;

    /**
     * Количество скрытых чипсов в фильтре
     */
    public chipsHidden: number = 0;

    /** Получить меню фильтра */
    public get menuFilter() {
        return this._menuFilter;
    }

    constructor(
        public appService: AppService,
        private formBuilder: FormBuilder,
        public datePipe: DatePipe,
        private notifier: NotifierService,
        public tableForm: TableComponent,
        public spinner: NgxSpinnerService,
        public factorRowService: FactorRowService,
    ) {}

    ngOnInit() {
        this.getRegisters();
    }

    ngOnChanges(changes: SimpleChanges) {
        // Форма и ее настройки обновились (например, при смене сохраненных поисков)
        if (changes.formDynamic && changes.settingData) {
            this.#onFormChanged();
        }
    }

    /**
     * Если форма обновилась, нужно построить новую
     * @private
     */
    #onFormChanged() {
        if (this.formDynamic && this.settingData) {
            // Нужен чтобы точно обновились данные
            setTimeout(() => {
                this.createForm(this.settingData);
            }, 0);
        }
    }

    public createForm(data): void {
        this.entityGrid = [];
        if (data && data.settings && data.settings.length) {
            this.entityGrid = data.settings;
            this.entityGrid.forEach((entity) => {
                entity.style = {
                    'grid-area': AppHelper.calculateGridArea({
                        cols: entity.cols,
                        rows: entity.rows,
                        x: entity.x,
                        y: entity.y,
                    }),
                };
                if (entity.fields && entity.fields.length) {
                    this.fieldGrid[entity.name] = entity.fields;
                    this.fieldGrid[entity.name].forEach((field) => {
                        field.style = {
                            'grid-area': AppHelper.calculateGridArea({
                                cols: field.cols,
                                rows: field.rows,
                                x: field.x,
                                y: field.y,
                            }),
                        };
                        this.addFormField(entity.name, field);
                    });
                }
            });
        }
        this.isFormData();
        this.isInitForm = true;
    }

    /**
     * Добавление на форму поля
     * @param name имя
     * @param field поле
     */
    addFormField(name, field) {
        let value =
            this.settingData.query && this.settingData.query[field.name] ? this.settingData.query[field.name] : null;
        // Если пришло true / false
        if (this.settingData.query && typeof this.settingData.query[field.name] == 'boolean') {
            value = this.settingData.query[field.name];
        }
        // Если пришло 1 / 0
        else if (
            this.settingData.query &&
            (this.settingData.query[field.name] == '0' || this.settingData.query[field.name] == '1')
        ) {
            value = this.settingData.query[field.name];
        }
        if (field.filter && field.filter.type == this.fieldsTypes.digit) {
            const formGroup = this.formBuilder.group({});
            this.formDynamic.addControl(field.name, formGroup);
            formGroup.addControl(
                'from',
                this.formBuilder.control(value && value.from ? value.from : null, [
                    (control: AbstractControl) =>
                        Validators.max(formGroup.get('to') ? formGroup.get('to').value : null)(control),
                ]),
            );
            formGroup.addControl(
                'to',
                this.formBuilder.control(value && value.to ? value.to : null, [
                    (control: AbstractControl) =>
                        Validators.min(formGroup.get('from') ? formGroup.get('from').value : null)(control),
                ]),
            );
        } else if (field.filter && field.filter.type == this.fieldsTypes.technicalProcess) {
            const formGroup = this.formBuilder.group({});
            this.formDynamic.addControl(field.name, formGroup);
            formGroup.addControl('tp', this.formBuilder.control(value && value.tp ? value.tp : null, []));
            formGroup.addControl(
                'regulation',
                this.formBuilder.control(value && value.regulation ? value.regulation : null, []),
            );
        } else if (field.filter && field.filter.type == this.fieldsTypes.access) {
            const formGroup = this.formBuilder.group({});
            this.formDynamic.addControl(field.name, formGroup);
            formGroup.addControl('user', this.formBuilder.control(value && value.user ? value.user : [], []));
            formGroup.addControl('subject', this.formBuilder.control(value && value.subject ? value.subject : [], []));
        } else if (field.filter && field.filter.type == this.fieldsTypes.address) {
            this.isFormAddress = true;
            const formGroup = this.formBuilder.group({
                region: [value && value.region ? value.region : [], []],
                district: [value && value.district ? value.district : [], []],
                city: [value && value.city ? value.city : [], []],
                region_name: [value && value.region_name ? value.region_name : null, []],
                district_name: [value && value.district_name ? value.district_name : null, []],
                city_name: [value && value.city_name ? value.city_name : null, []],
                street_1: [value && value.street_1 ? value.street_1 : null, []],
                street_2: [value && value.street_2 ? value.street_2 : null, []],
                street_3: [value && value.street_3 ? value.street_3 : null, []],
                including_additional_information: [
                    value && value.including_additional_information ? value.including_additional_information : false,
                    [],
                ],
                exact_match: [value && value.exact_match ? value.exact_match : false, []],
                building: [value && value.building ? value.building : null, []],
                apartment: [value && value.apartment ? value.apartment : null, []],
            });
            this.formDynamic.addControl(field.name, formGroup);
            if (value && value.region && value.region.length) {
                setTimeout(() => {
                    this.generateOptionsAddress(
                        field.name,
                        'region',
                        value && value.region ? value.region : null,
                        true,
                    );
                });
            }
            if (value && value.district && value.district.length) {
                setTimeout(() => {
                    this.generateOptionsAddress(
                        field.name,
                        'district',
                        value && value.district ? value.district : null,
                        true,
                    );
                });
            }
            formGroup.get('region').valueChanges.subscribe((value) => {
                if (!this.isSetAddressChange) {
                    this.generateOptionsAddress(field.name, 'region', value);
                }
            });
            formGroup.get('district').valueChanges.subscribe((value) => {
                if (!this.isSetAddressChange) {
                    this.generateOptionsAddress(field.name, 'district', value);
                }
            });
            formGroup.get('city').valueChanges.subscribe((value) => {
                if (!this.isSetAddressChange) {
                    formGroup?.get('city_name')?.setValue(this.getAddressNamesByValue(value));
                }
            });
        } else if (field.filter && field.filter.type == this.fieldsTypes.date) {
            const formGroup = this.formBuilder.group({});
            this.formDynamic.addControl(field.name, formGroup);
            formGroup.addControl('from', this.formBuilder.control(value && value.from ? value.from : null, []));
            formGroup.addControl('to', this.formBuilder.control(value && value.to ? value.to : null, []));
        } else {
            this.formDynamic.addControl(field.name, this.formBuilder.control(value, []));
        }
    }

    /**
     * Получение справочников
     */
    getRegisters() {
        if (this.settingData.scopes && this.settingData.scopes.length) {
            this.appService
                .getRegisters(this.settingData.scopes)
                .toPromise()
                .then((result: any) => {
                    if (result && result.is_error == false) {
                        this.registers = result.data;
                        this.createForm(this.settingData);
                    } else {
                        this.notifier.notify('error', result.message);
                    }
                })
                .catch(() => {
                    this.notifier.notify('error', this.appService.language_dictionary.system_no_connection_with_system);
                });
        } else {
            this.createForm(this.settingData);
        }
    }

    /**
     * Получение значения поля
     * @param field поле
     * @param form форма
     */
    getValueItem(field: any, form: FormGroup) {
        const value = [];
        let val = [];

        if (!field.filter) {
            return '';
        }

        switch (field.filter.type) {
            case EFieldsTypes.registry:
                val = [];
                if (form.get(field.name).value.length) {
                    form.get(field.name).value.forEach((id) => {
                        const list = this.registers[field.filter.scope].find((row) => row.id == id);
                        if (list) {
                            val.push(list.name);
                        }
                    });
                }
                value.push(val.join(', '));
                break;
            case EFieldsTypes.list:
                val = [];
                if (form.get(field.name).value?.length) {
                    form.get(field.name).value.forEach((id) => {
                        const list = field.filter.values.find((row) => row.id == id);
                        if (list) {
                            val.push(list.name);
                        }
                    });
                }
                value.push(val.join(', '));
                break;
            case EFieldsTypes.technical_process:
                if (form.get(field.name).get('tp').value) {
                    value.push(form.get(field.name).get('tp').value);
                }
                if (form.get(field.name).get('regulation').value) {
                    value.push(form.get(field.name).get('regulation').value);
                }
                break;
            case EFieldsTypes.bool:
                /* Если пришло true / false */
                if (typeof form.get(field.name).value == 'boolean') {
                    value.push(
                        form.get(field.name).value
                            ? this.appService.language_dictionary.system_yes
                            : this.appService.language_dictionary.system_no,
                    );
                } else if (form.get(field.name).value == '1' || form.get(field.name).value == '0') {
                    /* Если пришло 1/0 */
                    value.push(
                        form.get(field.name).value == '1'
                            ? this.appService.language_dictionary.system_yes
                            : this.appService.language_dictionary.system_no,
                    );
                }
                break;
            case EFieldsTypes.date:
                if (form.get(field.name).get('from').value) {
                    value.push(
                        this.appService.language_dictionary.system_from +
                            ' ' +
                            this.date(form.get(field.name).get('from').value),
                    );
                }
                if (form.get(field.name).get('to').value) {
                    value.push(
                        this.appService.language_dictionary.system_to +
                            ' ' +
                            this.date(form.get(field.name).get('to').value),
                    );
                }
                break;
            case EFieldsTypes.digit:
                if (form.get(field.name).get('from').value) {
                    value.push(
                        this.appService.language_dictionary.system_from + ' ' + form.get(field.name).get('from').value,
                    );
                }
                if (form.get(field.name).get('to').value) {
                    value.push(
                        this.appService.language_dictionary.system_to + ' ' + form.get(field.name).get('to').value,
                    );
                }
                break;
            case EFieldsTypes.access:
                const access = [];
                const subjectVal = form.get(field.name).get('subject').value;
                const subjectListName = 'user_access_level_subject(' + field?.user_access_id + ')';
                if (form.get(field.name).get('subject').value && form.get(field.name).get('subject').value.length) {
                    access.push(
                        this.appService.language_dictionary.system_subject +
                            ': ' +
                            this.getRegistersTextByIds(subjectListName, subjectVal),
                    );
                }
                if (form.get(field.name).get('user').value && form.get(field.name).get('user').value.length) {
                    access.push(
                        this.appService.language_dictionary.system_user +
                            ': ' +
                            this.getRegistersTextByIds('users', form.get(field.name).get('user').value),
                    );
                }
                if (access.length) {
                    value.push(access.join(', '));
                }
                break;
            case EFieldsTypes.address:
                const address = [];
                if (form?.get(field.name)?.get('region_name')?.value) {
                    address.push(
                        this.appService.language_dictionary.address_dialog_region +
                            ': ' +
                            form.get(field.name).get('region_name').value,
                    );
                }
                if (form?.get(field.name)?.get('district_name')?.value) {
                    address.push(
                        this.appService.language_dictionary.address_dialog_district +
                            ': ' +
                            form.get(field.name).get('district_name').value,
                    );
                }
                if (form?.get(field.name)?.get('city_name')?.value) {
                    address.push(
                        this.appService.language_dictionary.address_dialog_city +
                            ': ' +
                            form.get(field.name).get('city_name').value,
                    );
                }
                const street = [];
                if (form?.get(field.name)?.get('street_1')?.value) {
                    street.push(form.get(field.name).get('street_1').value);
                }
                if (form?.get(field.name)?.get('street_2')?.value) {
                    street.push(form.get(field.name).get('street_2').value);
                }
                if (form?.get(field.name)?.get('street_3')?.value) {
                    street.push(form.get(field.name).get('street_3').value);
                }
                if (street.length) {
                    address.push(this.appService.language_dictionary.address_dialog_street + ': ' + street.join(', '));
                }
                if (form?.get(field.name)?.get('building')?.value) {
                    address.push(
                        this.appService.language_dictionary.address_dialog_building +
                            ': ' +
                            form.get(field.name).get('building').value,
                    );
                }
                if (form?.get(field.name)?.get('apartment')?.value) {
                    address.push(
                        this.appService.language_dictionary.address_dialog_apartment +
                            ': ' +
                            form.get(field.name).get('apartment').value,
                    );
                }
                value.push(address.join(', '));
                break;
            default:
                const formVal = form.get(field.name).value;
                if (typeof formVal !== 'object') {
                    value.push(form.get(field.name).value);
                }
        }

        return value.join(' ');
    }

    /**
     * Получение списка записей по ИД
     * @param register список
     * @param ids ИД записей
     */
    getRegistersTextByIds(register: string, ids: any[]) {
        const result = [];
        this.registers[register]?.forEach((item) => {
            if (ids.indexOf(item.id) != -1) {
                result.push(item.name);
            }
        });
        return result.join(', ');
    }

    /**
     * Форматер даты
     */
    date(input: any) {
        return input && moment(input).isValid() ? this.datePipe.transform(input, 'shortDate') : '';
    }

    /**
     * Проставление флага формы
     * //TODO: не уверена что это нужно в таком виде
     */
    isFormData(flag = false) {
        Object.keys(this.formDynamic.controls).forEach((key) => {
            const form = this.formDynamic.get(key) as FormGroup;
            if (form.controls && Object.keys(form.controls).length) {
                Object.keys(form.controls).forEach((child) => {
                    if (typeof form.get(child)?.value === 'object' && form.get(child)?.value?.length) {
                        this.formFilter.isOpenFilter = flag;
                    } else if (form.get(child)?.value && typeof form.get(child)?.value !== 'object') {
                        this.formFilter.isOpenFilter = flag;
                    }
                });
            } else if (form.value) {
                this.formFilter.isOpenFilter = flag;
            }
        });
    }

    /**
     * Проверка пустоты поля
     */
    isFieldData(form: FormGroup | any) {
        let isData = false;
        if (form.controls && Object.keys(form.controls).length) {
            Object.keys(form.controls).forEach((key) => {
                if (typeof form.get(key)?.value == 'object' && form.get(key)?.value?.length) {
                    isData = true;
                } else if (form.get(key).value && typeof form.get(key).value != 'object') {
                    isData = true;
                }
            });
        } else if (form.value != null) {
            isData = true;
        }
        return isData;
    }

    /**
     * Проверка пустоты блока фильтра
     * @param fields массив полей
     */
    isSubFormData(fields: any[]) {
        let isNotEmpty = false;
        fields.forEach((field) => {
            if (this.isFieldData(this.formDynamic.get(field.name))) {
                isNotEmpty = true;
            }
        });
        return isNotEmpty;
    }

    /**
     * Определяем наличие формы показа фильтра
     */
    isSubForm() {
        return this.entityGrid.some(
            (item) =>
                this.fieldGrid[item.name] &&
                this.fieldGrid[item.name].length > 0 &&
                this.isSubFormData(this.fieldGrid[item.name]),
        );
    }

    /**
     * Очистка поля формы
     * @param field поле формы
     */
    clearField(field: FormGroup | any) {
        if (field.controls && Object.keys(field.controls).length) {
            Object.keys(field.controls).forEach((key) => {
                field.get(key).setValue(null);
            });
        } else {
            field.setValue(null);
        }
        if (!this.isSubForm()) {
            this.tableForm.clearExtendFilter();
            this.formFilter.isFilterSet = false;
        }
        this.setFilter.next();
        this.formFilter.setFilterChanged();
    }

    /**
     * Генерация опций селекта адреса
     * @param name имя поля
     * @param type тип поля
     * @param value значение селекта
     * @param isInit флаг инициализации
     * //TODO: сделать отдельный сервис для адреса
     */
    generateOptionsAddress(name: string, type: string, value: string[], isInit: boolean = false) {
        this.isSetAddressChange = true;
        let layers = null;
        if (type === 'region') {
            layers = '2,3,4';
        }
        if (type === 'district') {
            layers = '4,5,6';
        }
        if (value && value.length) {
            this.isLoadAddress.city[name] = true;
            if (type === 'region') {
                this.isLoadAddress.district[name] = true;
            }
            this.appService
                .getAddressFIAS('get_regions', {
                    levels: layers,
                    type: 'municipal',
                    parents: value,
                })
                .subscribe((result) => {
                    if (type === 'region') {
                        this.addressData.district[name + '_district'] = [];
                        this.addressData.city[name + '_city'] = [];
                        this.addressData.city[name + '_city_region'] = [];
                        result.forEach((region) => {
                            if (region.level === 4) {
                                if (region.parents.length === 2) {
                                    this.addressData.city[name + '_city'].push({
                                        id: region.id,
                                        name: region.fullname,
                                    });
                                    this.addressData.city[name + '_city_region'].push({
                                        id: region.id,
                                        name: region.fullname,
                                    });
                                }
                            } else {
                                this.addressData.district[name + '_district'].push({
                                    id: region.id,
                                    name: region.fullname,
                                });
                            }
                            this.addressDataName[region.id] = region.fullname;
                        });
                        this.changeSelectsByTypes(name, type, isInit);
                        if (!isInit) {
                            this.formDynamic
                                ?.get(name)
                                ?.get('region_name')
                                ?.setValue(this.getAddressNamesByValue(value));
                        }
                    }
                    if (type === 'district') {
                        this.addressData.city[name + '_city'] = this.addressData?.city[name + '_city_region']
                            ? this.addressData?.city[name + '_city_region'].slice()
                            : [];
                        result.forEach((region) => {
                            this.addressData.city[name + '_city'].push({ id: region.id, name: region.fullname });
                            this.addressDataName[region.id] = region.fullname;
                        });
                        this.changeSelectsByTypes(name, type, isInit);
                        if (!isInit) {
                            this.formDynamic
                                ?.get(name)
                                ?.get('district_name')
                                ?.setValue(this.getAddressNamesByValue(value));
                        }
                    }
                    this.isSetAddressChange = false;
                    this.isLoadAddress.city[name] = false;
                    if (type === 'region') {
                        this.isLoadAddress.district[name] = false;
                    }
                });
        } else {
            if (type === 'region') {
                this.addressData.district[name + '_district'] = [];
                this.addressData.city[name + '_city_region'] = [];
            }
            this.addressData.city[name + '_city'] = [];
            if (type === 'district' && this.addressData?.city[name + '_city_region']?.length) {
                this.addressData.city[name + '_city'] = this.addressData?.city[name + '_city_region'].slice();
            }
            this.changeSelectsByTypes(name, type, isInit);
            if (!isInit) {
                this.formDynamic
                    ?.get(name)
                    ?.get(type + '_name')
                    ?.setValue(null);
            }
            this.isSetAddressChange = false;
        }
    }

    /**
     * Изменение селектов по типу
     * @param name имя поля
     * @param type тип
     * @param isInit флаг инициализации
     */
    changeSelectsByTypes(name: string, type: string, isInit: boolean = false) {
        this.changeOptionsAddress(name + '_city', this.addressData.city[name + '_city']);
        if (type == 'region') {
            this.changeOptionsAddress(name + '_district', this.addressData.district[name + '_district']);
            if (!isInit) {
                this.formDynamic?.get(name)?.get('district')?.setValue([]);
                this.formDynamic?.get(name)?.get('city')?.setValue([]);
                this.formDynamic?.get(name)?.get('district_name')?.setValue(null);
                this.formDynamic?.get(name)?.get('city_name')?.setValue(null);
            }
        }
        if (type == 'district') {
            if (!isInit) {
                this.formDynamic?.get(name)?.get('city')?.setValue([]);
                this.formDynamic?.get(name)?.get('city_name')?.setValue(null);
            }
        }
    }

    /**
     * Изменение опций селекта адреса
     * @param name имя поля
     * @param options опции
     */
    changeOptionsAddress(name: string, options: any[]) {
        if (this.dynamicFields) {
            this.dynamicFields.forEach((fieldItem: any) => {
                if (fieldItem.id && fieldItem.id == name) {
                    fieldItem.setOptions(options);
                }
            });
        }
    }

    /**
     * Получение имен адресов по набору UUID
     * @param value набор UUID
     */
    getAddressNamesByValue(value: string[]) {
        const address = [];
        if (value && value.length) {
            value.forEach((v) => {
                if (this.addressDataName[v]) {
                    address.push(this.addressDataName[v]);
                }
            });
        }
        return address.join(', ');
    }

    /**
     * Обработка нажатия на поиск
     * @param showForm
     */
    public clickSearch(showForm: boolean): void {
        if (showForm) {
            this.formFilter.isOpenFilter = false;
        } else {
            this.tableForm.isSpinnerFullScreen = true;
            this.tableForm.setExtendFilter();
        }
    }

    /** Обработка нажатия на очистить */
    public clickClear(): void {
        this.tableForm.clearExtendFilter();
    }

    /**
     * Очистить критерии поиска
     */
    public clearCriteria(): void {
        this.tableForm.resetExtendFilter();
    }

    ngAfterViewInit() {
        /* Запрашиваем фиас один раз при открытии фильтра */
        this.formFilter.isOpenFilter$.pipe(takeUntil(this.#destroy$)).subscribe((val) => {
            if (!this.isRegionRequested && val && this.isFormAddress) {
                this.isRegionRequested = true;
                this.addressData.region = [];
                this.appService
                    .getAddressFIAS('get_regions', {})
                    .pipe(takeUntil(this.#destroy$))
                    .subscribe({
                        next: (result) => {
                            this.spinner.hide();
                            this.firstOpen = false;
                            result.forEach((region) => {
                                this.addressData.region.push({ id: region.id, name: region.fullname });
                                this.addressDataName[region.id] = region.fullname;
                            });
                            if (this.dynamicFields) {
                                this.dynamicFields.forEach((fieldItem: any) => {
                                    if (fieldItem.id && fieldItem.id.match('(_region)$')) {
                                        fieldItem.setOptions(this.addressData.region);
                                    }
                                });
                            }
                            this.isRegionRequested = true;
                        },
                        error: () => {
                            this.spinner.hide();
                            this.addressData.region = [];
                            this.notifier.notify(
                                ENotifyTypes.warning,
                                this.appService.language_dictionary?.fias_service_is_unavailable,
                            );
                            this.isRegionRequested = false;
                        },
                    });
            }
        });
    }
}
